import { cva, type VariantProps } from 'class-variance-authority';
import type * as React from 'react';
import { cn } from '../lib/cn.ts';

const badgeVariants = cva(
  [
    'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5',
    'font-[var(--font-mono)] text-xs font-medium leading-tight',
    'border',
  ],
  {
    variants: {
      variant: {
        default: [
          'bg-[var(--color-charcoal)] text-[var(--color-alabaster)]',
          'border-[var(--color-charcoal)]',
        ],
        secondary: [
          'bg-[var(--color-canvas)] text-[var(--color-charcoal)]',
          'border-[var(--color-warm-stone-soft)]',
        ],
        outline: [
          'bg-transparent text-[var(--color-charcoal)]',
          'border-[var(--color-warm-stone-soft)]',
        ],
        success: [
          'bg-[var(--color-success)]/10 text-[var(--color-success)]',
          'border-[var(--color-success)]/20',
        ],
        destructive: [
          'bg-[var(--color-alert)]/10 text-[var(--color-alert)]',
          'border-[var(--color-alert)]/20',
        ],
        muted: ['bg-[var(--color-muted)] text-[var(--color-stone-mid)]', 'border-transparent'],
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {
  'data-testid'?: string;
}

function Badge({ className, variant, ...props }: BadgeProps) {
  return <span className={cn(badgeVariants({ variant }), className)} {...props} />;
}

export { Badge, badgeVariants };
